/**
 * University List Component - Modern Design
 * Displays university cards with modern styling and pagination
 */

import React from 'react';
import { Button } from '@voilajsx/uikit/button';
import { Card, CardHeader, CardTitle, CardContent } from '@voilajsx/uikit/card';
import { Badge } from '@voilajsx/uikit/badge';
import { Loader2, MapPin, Users, DollarSign, ChevronLeft, ChevronRight, ExternalLink, GraduationCap, TrendingUp } from 'lucide-react';
import type { University } from '../types';

interface UniversityListProps {
  universities: University[];
  pagination: {
    page: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onUniversityClick: (slug: string) => void;
}

export const UniversityList: React.FC<UniversityListProps> = ({
  universities,
  pagination,
  isLoading,
  onPageChange,
  onUniversityClick
}) => {
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--color-primary)' }} />
          <span style={{
            color: '#0F172A',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            Loading universities...
          </span>
        </div>
      </div>
    );
  }

  if (universities.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '80px 20px',
        background: '#FFFFFF',
        borderRadius: '20px',
        border: '1px solid #E2E8F0'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: '#F1F5F9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px'
        }}>
          <GraduationCap className="h-8 w-8" style={{ color: '#64748B' }} />
        </div>
        <p style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#0F172A',
          marginBottom: '8px'
        }}>
          No universities found
        </p>
        <p style={{
          fontSize: '14px',
          color: '#64748B'
        }}>
          Try adjusting your search criteria or removing some filters.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* University Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
        {universities.map((university) => (
          <div
            key={university.id}
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '28px',
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
              border: '1px solid #E2E8F0',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            onClick={() => university.slug && onUniversityClick(university.slug)}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(15, 23, 42, 0.12)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(15, 23, 42, 0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '20px',
              marginBottom: '24px'
            }}>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '22px',
                  fontWeight: '700',
                  color: '#0F172A',
                  marginBottom: '12px',
                  letterSpacing: '-0.01em',
                  lineHeight: '1.3'
                }}>
                  {university.name}
                </h3>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '16px',
                  fontSize: '14px',
                  color: '#64748B'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '18px' }}>{university.country.flag_emoji}</span>
                    <span style={{ fontWeight: '500' }}>{university.country.name}</span>
                  </div>
                  {university.city && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <MapPin className="h-4 w-4" />
                      <span>{university.city}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Ranking Badge */}
              {university.qs_world_ranking && (
                <div style={{
                  background: '#FEF3C7',
                  color: '#92400E',
                  padding: '8px 16px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  border: '1px solid #FCD34D'
                }}>
                  <TrendingUp className="h-4 w-4" />
                  #{university.qs_world_ranking}
                </div>
              )}
            </div>

            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              {university.total_students && (
                <div style={{
                  background: '#F8FAFC',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #E2E8F0'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'var(--color-primary)/10',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Users className="h-4 w-4" style={{ color: 'var(--color-primary)' }} />
                    </div>
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#0F172A',
                    marginBottom: '4px'
                  }}>
                    {university.total_students.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748B', fontWeight: '500' }}>
                    Students
                  </div>
                </div>
              )}

              {university.tuition_usd_min && (
                <div style={{
                  background: '#F0FDF4',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #BBF7D0'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: '#DCFCE7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <DollarSign className="h-4 w-4" style={{ color: '#059669' }} />
                    </div>
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#0F172A',
                    marginBottom: '4px'
                  }}>
                    ${(university.tuition_usd_min / 1000).toFixed(0)}k
                    {university.tuition_usd_max &&
                      ` - ${(university.tuition_usd_max / 1000).toFixed(0)}k`}
                  </div>
                  <div style={{ fontSize: '13px', color: '#059669', fontWeight: '500' }}>
                    Tuition/year
                  </div>
                </div>
              )}

              {university.acceptance_rate && (
                <div style={{
                  background: '#FEF3C7',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #FCD34D'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: '#FDE68A',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#92400E'
                    }}>
                      %
                    </div>
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#0F172A',
                    marginBottom: '4px'
                  }}>
                    {university.acceptance_rate}%
                  </div>
                  <div style={{ fontSize: '13px', color: '#92400E', fontWeight: '500' }}>
                    Acceptance
                  </div>
                </div>
              )}

              {university.international_students && (
                <div style={{
                  background: '#F5F3FF',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #DDD6FE'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: '#EDE9FE',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px'
                    }}>
                      🌍
                    </div>
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#0F172A',
                    marginBottom: '4px'
                  }}>
                    {university.international_students.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--color-primary)', fontWeight: '500' }}>
                    International
                  </div>
                </div>
              )}
            </div>

            {/* Programs Preview */}
            {university.programs.length > 0 && (
              <div style={{
                borderTop: '1px solid #E2E8F0',
                paddingTop: '20px',
                marginBottom: '20px'
              }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '700',
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <GraduationCap className="h-4 w-4" />
                  Available Programs ({university.programs.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {university.programs.slice(0, 3).map((program) => (
                    <div
                      key={program.id}
                      style={{
                        background: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#0F172A'
                      }}
                    >
                      {program.field} - {program.degree_type}
                    </div>
                  ))}
                  {university.programs.length > 3 && (
                    <div style={{
                      background: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      padding: '6px 12px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#64748B'
                    }}>
                      +{university.programs.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Website Link */}
            {university.website_url && (
              <div style={{
                borderTop: '1px solid #E2E8F0',
                paddingTop: '16px'
              }}>
                <a
                  href={university.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--color-primary)',
                    textDecoration: 'none',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  <ExternalLink className="h-4 w-4" />
                  Visit Website
                </a>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '20px 24px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)'
        }}>
          <div style={{
            fontSize: '14px',
            color: '#64748B',
            fontWeight: '500'
          }}>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total.toLocaleString()} total)
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={!pagination.hasPrev}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 18px',
                background: pagination.hasPrev ? '#F8FAFC' : '#F1F5F9',
                border: '1px solid #E2E8F0',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                color: pagination.hasPrev ? '#0F172A' : '#94A3B8',
                cursor: pagination.hasPrev ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                opacity: pagination.hasPrev ? 1 : 0.5
              }}
              onMouseEnter={(e) => {
                if (pagination.hasPrev) {
                  e.currentTarget.style.background = 'var(--color-primary)';
                  e.currentTarget.style.color = '#FFFFFF';
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (pagination.hasPrev) {
                  e.currentTarget.style.background = '#F8FAFC';
                  e.currentTarget.style.color = '#0F172A';
                  e.currentTarget.style.borderColor = '#E2E8F0';
                }
              }}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>

            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={!pagination.hasNext}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 18px',
                background: pagination.hasNext ? 'var(--color-primary)' : '#F1F5F9',
                border: pagination.hasNext ? '1px solid var(--color-primary)' : '1px solid #E2E8F0',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                color: pagination.hasNext ? '#FFFFFF' : '#94A3B8',
                cursor: pagination.hasNext ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                opacity: pagination.hasNext ? 1 : 0.5
              }}
              onMouseEnter={(e) => {
                if (pagination.hasNext) {
                  e.currentTarget.style.background = 'var(--color-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (pagination.hasNext) {
                  e.currentTarget.style.background = 'var(--color-primary)';
                }
              }}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
